import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { listPublishedPosts } from "@/lib/blog.functions";

const postsQuery = queryOptions({
  queryKey: ["published-posts"],
  queryFn: () => listPublishedPosts(),
});

export const Route = createFileRoute("/blog/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQuery),
  component: BlogIndex,
  head: () => ({
    meta: [
      { title: "MOTM Insights - Real Estate Coaching Blog" },
      { name: "description", content: "MOTM Insights is the Kristan Cole real estate coaching blog: a weekly Message of the Moment on recruiting, coaching, and CEO-level growth for agents." },
      { property: "og:title", content: "MOTM Insights - Kristan Cole Network" },
      { property: "og:description", content: "Weekly Message of the Moment from Kristan Cole and the KCN team." },
      { property: "og:url", content: "https://joinkcn.com/blog" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/blog" }],
    scripts: [
      breadcrumbSchema([{ name: "Blog", path: "/blog" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "MOTM Insights - Kristan Cole Network",
          url: "https://joinkcn.com/blog",
        }),
      },
    ],
  }),
  errorComponent: ({ error }) => <SiteLayout><Section><p className="text-primary">Couldn't load posts: {error instanceof Error ? error.message : "Unknown error"}</p></Section></SiteLayout>,
  notFoundComponent: () => <SiteLayout><Section><p>Not found.</p></Section></SiteLayout>,
});

function BlogIndex() {
  const { data: posts } = useSuspenseQuery(postsQuery);
  return (
    <SiteLayout>
      <Section tone="dark" className="pt-36 md:pt-36 lg:pt-36">
        <SectionEyebrow>MOTM Insights</SectionEyebrow>
        <h1 className="headline-xl mb-6">Message of the Moment</h1>
        <p className="text-lg text-foreground/75 max-w-2xl mb-16">
          Weekly field notes from a 40-year career: how top agents build teams, scale leverage, and create legacy.
        </p>

        {posts.length === 0 ? (
          <p className="text-foreground/65">New posts publishing soon.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
            {posts.map((p: any) => (
              <article key={p.id} className="group h-full">
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="flex h-full flex-col border border-border bg-surface p-6 transition-colors hover:border-primary group-hover:bg-foreground group-hover:text-background"
                >
                  <p className="eyebrow mb-4 group-hover:text-background/70">
                    {new Date(p.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                  <div className="font-display text-base md:text-lg leading-snug mb-3 group-hover:text-primary transition-colors">
                    {p.title}
                  </div>
                  {p.excerpt ? (
                    <p className="text-foreground/75 text-sm leading-relaxed line-clamp-3 group-hover:text-background/80">{p.excerpt}</p>
                  ) : null}
                  <span className="mt-auto pt-6 text-xs uppercase tracking-[0.18em] text-foreground/65 group-hover:text-primary transition-colors">
                    Read insight →
                  </span>
                </Link>
              </article>
            ))}
          </div>
        )}
      </Section>
    </SiteLayout>
  );
}
